// Shop Homepage - Customer-facing product catalog
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import ProductCard from '../components/ProductCard';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [showSoldOut, setShowSoldOut] = useState(false);
  const [searchParams] = useSearchParams();
  const highlightedProductRef = useRef(null);

  // SEO metadata
  useSEO({
    title: 'Shop Handcrafted Earrings - Magnolia Mold Co',
    description: 'Browse our collection of unique, handcrafted polymer clay earrings. Lightweight, comfortable, and made with love. Free shipping on orders over $50.',
    url: 'https://magnoliamoldco.com/shop',
    keywords: 'buy polymer clay earrings, handmade earrings shop, lightweight earrings, unique jewelry, custom earrings'
  });

  useEffect(() => {
    loadProducts();
  }, []);

  // Handle SKU from QR code scan
  useEffect(() => {
    const skuParam = searchParams.get('sku');
    if (skuParam) {
      setSearchTerm(skuParam);
      // Scroll to highlighted product after a brief delay
      setTimeout(() => {
        if (highlightedProductRef.current) {
          highlightedProductRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [searchParams, products]);

  const loadProducts = async () => {
    try {
      // Use public API (no authentication needed)
      const response = await fetch(`${API_URL}/api/public/products`);
      const data = await response.json();
      setProducts(data.products || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading products:', error);
      setIsLoading(false);
    }
  };

  // Get unique product types
  const types = ['all', ...new Set(products.map(p => p.name))].sort();

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || product.name === selectedType;
    
    // Filter out sold-out items unless showSoldOut is true
    const matchesStock = showSoldOut || product.inStock;
    
    return matchesSearch && matchesType && matchesStock;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cream-300 border-t-sage-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Page header */}
      <div className="bg-cream-50 border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 text-center">
          <p className="eyebrow mb-3">The Collection</p>
          <h1 className="section-title mb-4">Handcrafted Earrings</h1>
          <div className="flourish mb-4" aria-hidden="true">✦</div>
          <p className="text-lg text-ink-soft">Unique, beautiful designs made with love</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <div className="relative flex-1">
            <svg className="w-5 h-5 text-ink-muted absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search earrings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="field pl-11"
            />
          </div>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="field md:w-56"
            aria-label="Filter by type"
          >
            {types.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between mb-10 text-sm">
          <p className="text-ink-muted">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'piece' : 'pieces'}
          </p>
          <label className="flex items-center gap-2 cursor-pointer text-ink-soft">
            <input
              type="checkbox"
              checked={showSoldOut}
              onChange={(e) => setShowSoldOut(e.target.checked)}
              className="w-4 h-4 rounded border-cream-300 accent-sage-700"
            />
            Show sold out
          </label>
        </div>

        {/* Product Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-serif text-2xl text-ink mb-2">Nothing here just yet</p>
            <p className="text-ink-soft">Try a different search or type.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
            {filteredProducts.map((product) => {
              const isHighlighted = searchParams.get('sku') === product.sku;
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  isHighlighted={isHighlighted}
                  highlightedRef={isHighlighted ? highlightedProductRef : null}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
